import { useState, useRef } from "react";
import {
  X,
  Upload,
  FileSpreadsheet,
  Download,
  AlertCircle,
  CheckCircle2,
  Trash2,
  FileText,
  Check,
} from "lucide-react";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { errorMessage } from "../../lib/format";

const SAMPLE_DATA = [
  { Description: "Matte finish 350 GSM cards", Product: "Visiting Cards", Quantity: 500, Unit: "pcs", "Value/Price (Optional)": 2500 },
  { Description: "Tri-fold 4-color company brochure", Product: "Brochure", Quantity: 1000, Unit: "pcs", "Value/Price (Optional)": "" },
  { Description: "Custom printed corrugated box", Product: "Packaging Box", Quantity: 250, Unit: "boxes", "Value/Price (Optional)": 15000 },
];

function formatCurrency(amount) {
  if (amount === null || amount === undefined || amount === "" || isNaN(amount)) return "—";
  if (Number(amount) === 0) return "₹0";
  return `₹${Number(amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function BulkOrderUploadModal({ client, isOpen, onClose, onCreated, onOpenOrder }) {
  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  if (!isOpen || !client) return null;

  // Download Sample Excel/CSV File
  const handleDownloadSample = (format = "xlsx") => {
    try {
      const ws = XLSX.utils.json_to_sheet(SAMPLE_DATA);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Orders");

      if (format === "csv") {
        const csvContent =
          "Description,Product,Quantity,Unit,Value/Price\n" +
          "Matte finish 350 GSM cards,Visiting Cards,500,pcs,2500\n" +
          "Tri-fold 4-color company brochure,Brochure,1000,pcs,\n" +
          "Custom printed corrugated box,Packaging Box,250,boxes,15000\n";
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `sample_bulk_orders_${client.client_id}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        XLSX.writeFile(wb, `sample_bulk_orders_${client.client_id}.xlsx`);
      }
      toast.success(`Downloaded sample ${format.toUpperCase()} template`);
    } catch (err) {
      toast.error("Failed to generate sample template");
    }
  };

  // Parse Excel / CSV file using SheetJS
  const processFile = (selectedFile) => {
    if (!selectedFile) return;

    const ext = selectedFile.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) {
      toast.error("Please upload a valid .xlsx or .csv file");
      return;
    }

    setFile(selectedFile);
    setParsing(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        if (!json || json.length === 0) {
          toast.error("The uploaded file contains no data rows.");
          setParsedRows([]);
          setParsing(false);
          return;
        }

        // Validate and normalize columns
        const validated = json.map((row, index) => {
          // Normalize header keys case-insensitively
          const getVal = (keys) => {
            for (const k of Object.keys(row)) {
              const cleanK = k.trim().toLowerCase().replace(/[\s_/\\-]/g, "");
              if (keys.some((target) => target.replace(/[\s_/\\-]/g, "") === cleanK)) {
                return row[k];
              }
            }
            return "";
          };

          const rawDescription = getVal(["description", "desc", "details", "specifications", "spec"]);
          const rawProduct = getVal(["product", "product name", "item", "name"]);
          const rawQty = getVal(["quantity", "qty", "count"]);
          const rawUnit = getVal(["unit", "uom", "unit of measurement", "units"]);
          const rawValue = getVal(["value/price (optional)", "value/price", "value", "price", "amount", "total", "rate"]);

          const description = String(rawDescription || "").trim();
          const product = String(rawProduct || "").trim();
          const quantity = parseFloat(rawQty);
          const unit = String(rawUnit || "").trim();
          const rawValStr = String(rawValue !== undefined && rawValue !== null ? rawValue : "").trim();
          const value = rawValStr === "" ? 0 : parseFloat(rawValStr);

          const errors = [];
          if (!product) errors.push("Product name is required");
          if (isNaN(quantity) || quantity <= 0) errors.push("Quantity must be > 0");
          if (!unit) errors.push("Unit is required (e.g. pcs, boxes)");
          if (rawValStr !== "" && (isNaN(value) || value < 0)) errors.push("Value/Price must be ≥ 0");

          return {
            rowNumber: index + 1,
            description,
            product,
            quantity: isNaN(quantity) ? "" : quantity,
            unit: unit || "pcs",
            value: rawValStr === "" ? 0 : (isNaN(value) ? "" : value),
            hasPrice: rawValStr !== "" && !isNaN(value) && value > 0,
            isValid: errors.length === 0,
            errors,
          };
        });

        setParsedRows(validated);
      } catch (err) {
        toast.error("Failed to parse the file. Please check format.");
      } finally {
        setParsing(false);
      }
    };

    reader.readAsBinaryString(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const validRows = parsedRows.filter((r) => r.isValid);
  const invalidRows = parsedRows.filter((r) => !r.isValid);
  const totalValue = validRows.reduce((sum, r) => sum + (Number(r.value) || 0), 0);

  // Submit Bulk Orders
  const handleConfirmUpload = async () => {
    if (validRows.length === 0) {
      toast.error("No valid order rows to create");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        client_id: client.client_id,
        batch_name: file ? file.name : `Excel Upload (${validRows.length} Products)`,
        orders: validRows.map((r) => ({
          description: r.description,
          product: r.product,
          quantity: r.quantity,
          unit: r.unit,
          value: r.value,
        })),
      };

      const res = await api("post", "/orders/bulk", payload);
      const count = res.data?.created_count || validRows.length;
      toast.success(`Successfully created ${count} orders for ${client.company_name}`);

      if (onCreated) onCreated();
      onClose();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to create bulk orders"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setParsedRows([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div
      className="modal-backdrop"
      style={{ zIndex: 10000 }}
      data-testid="bulk-order-modal-backdrop"
    >
      <div
        className="modal"
        style={{ maxWidth: 680, width: "95%", padding: "20px 24px" }}
        data-testid="bulk-order-modal"
      >
        {/* Modal Head */}
        <div className="modal-head" style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 6,
                background: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--navy)",
              }}
            >
              <FileSpreadsheet size={18} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, color: "var(--navy)" }}>Upload Multiple Orders</h2>
              <div style={{ margin: "2px 0 0", fontSize: 12, color: "var(--muted)" }}>
                Client: <b>{client.company_name}</b> ({client.client_id})
              </div>
            </div>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            data-testid="close-bulk-order-modal"
          >
            <X size={18} />
          </button>
        </div>

        {/* Instructions & Sample Download Bar */}
        <div
          style={{
            background: "#f8fafc",
            border: "1px solid var(--border)",
            borderRadius: 6,
            padding: "10px 14px",
            marginBottom: 14,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <div style={{ fontSize: 12, color: "var(--muted)" }}>
            File columns: <b style={{ color: "var(--navy)" }}>Description</b>,{" "}
            <b style={{ color: "var(--navy)" }}>Product</b>,{" "}
            <b style={{ color: "var(--navy)" }}>Quantity</b>, <b style={{ color: "var(--navy)" }}>Unit</b>,{" "}
            <b style={{ color: "var(--navy)" }}>Value/Price (Optional)</b>
          </div>

          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              className="outline-btn compact"
              onClick={() => handleDownloadSample("xlsx")}
              data-testid="download-sample-xlsx-btn"
              style={{ height: 28, fontSize: 11, padding: "0 8px", display: "inline-flex", alignItems: "center", gap: 4 }}
              title="Download Sample Excel File (.xlsx)"
            >
              <Download size={11} /> Sample Excel (.xlsx)
            </button>
            <button
              type="button"
              className="outline-btn compact"
              onClick={() => handleDownloadSample("csv")}
              data-testid="download-sample-csv-btn"
              style={{ height: 28, fontSize: 11, padding: "0 8px", display: "inline-flex", alignItems: "center", gap: 4 }}
              title="Download Sample CSV File (.csv)"
            >
              <Download size={11} /> Sample CSV
            </button>
          </div>
        </div>

        {/* Upload Drop Zone (if no file or to re-upload) */}
        {!file && (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            style={{
              border: dragOver ? "2px dashed #2563eb" : "2px dashed var(--border)",
              background: dragOver ? "#eff6ff" : "#fbfcfe",
              borderRadius: 8,
              padding: "24px 16px",
              textAlign: "center",
              cursor: "pointer",
              marginBottom: 16,
              transition: "all 0.15s ease",
            }}
            data-testid="bulk-upload-dropzone"
          >
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              style={{ display: "none" }}
              data-testid="bulk-order-file-input"
            />
            <Upload size={24} style={{ color: "var(--muted)", margin: "0 auto 8px" }} />
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)", marginBottom: 2 }}>
              Click to browse or drag & drop Excel / CSV file
            </div>
            <div style={{ fontSize: 11, color: "var(--muted)" }}>Supports .xlsx, .xls, and .csv files</div>
          </div>
        )}

        {/* Selected File & Action Strip */}
        {file && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 12px",
              background: "#f1f5f9",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <FileSpreadsheet size={15} style={{ color: "var(--navy)" }} />
              <b style={{ color: "var(--navy)" }}>{file.name}</b>
              <span style={{ color: "var(--muted)", fontSize: 11 }}>
                ({(file.size / 1024).toFixed(1)} KB)
              </span>
            </div>
            <button
              type="button"
              className="outline-btn compact"
              onClick={handleReset}
              data-testid="bulk-order-reset-file-btn"
              style={{ height: 26, fontSize: 11, padding: "0 8px" }}
            >
              Choose different file
            </button>
          </div>
        )}

        {/* Preview Table */}
        {parsing && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--muted)", fontSize: 12 }}>
            Reading and validating file rows...
          </div>
        )}

        {!parsing && parsedRows.length > 0 && (
          <div>
            {/* Status Summary Banner */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 10px",
                background: invalidRows.length > 0 ? "#fffbeb" : "#ecfdf5",
                border: invalidRows.length > 0 ? "1px solid #fde68a" : "1px solid #bbf7d0",
                borderRadius: 4,
                fontSize: 12,
                marginBottom: 10,
              }}
            >
              <div>
                <b style={{ color: "#15803d" }}>{validRows.length} valid orders</b> ready to create
                {invalidRows.length > 0 && (
                  <span style={{ color: "#b45309", marginLeft: 6 }}>
                    ({invalidRows.length} invalid rows will be skipped)
                  </span>
                )}
              </div>
              <div style={{ fontWeight: 600, color: "var(--navy)" }}>
                Total Value: <strong style={{ color: "#15803d" }}>{formatCurrency(totalValue)}</strong>
              </div>
            </div>

            {/* Rows Table */}
            <div style={{ maxHeight: 250, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 4, marginBottom: 16 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#f8fafc", borderBottom: "1px solid var(--border)", textAlign: "left" }}>
                    <th style={{ padding: "6px 8px", fontWeight: 600, color: "var(--muted)", fontSize: 11, width: 30 }}>#</th>
                    <th style={{ padding: "6px 8px", fontWeight: 600, color: "var(--muted)", fontSize: 11, minWidth: 160 }}>Description</th>
                    <th style={{ padding: "6px 8px", fontWeight: 600, color: "var(--muted)", fontSize: 11, width: 130 }}>Product</th>
                    <th style={{ padding: "6px 8px", fontWeight: 600, color: "var(--muted)", fontSize: 11, width: 75 }}>Quantity</th>
                    <th style={{ padding: "6px 8px", fontWeight: 600, color: "var(--muted)", fontSize: 11, width: 65 }}>Unit</th>
                    <th style={{ padding: "6px 8px", fontWeight: 600, color: "var(--muted)", fontSize: 11, width: 95 }}>Value/Price</th>
                    <th style={{ padding: "6px 8px", fontWeight: 600, color: "var(--muted)", fontSize: 11, width: 90 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedRows.map((r) => (
                    <tr
                      key={r.rowNumber}
                      style={{
                        borderBottom: "1px solid var(--border)",
                        background: r.isValid ? "#fff" : "#fef2f2",
                      }}
                      data-testid={`bulk-preview-row-${r.rowNumber}`}
                    >
                      <td style={{ padding: "6px 8px", color: "var(--muted)", fontSize: 11 }}>{r.rowNumber}</td>
                      <td style={{ padding: "6px 8px", color: "#475569", fontSize: 11.5, minWidth: 160 }}>
                        {r.description || <span style={{ color: "#94a3b8" }}>—</span>}
                      </td>
                      <td style={{ padding: "6px 8px", fontWeight: 500, color: "var(--navy)", width: 130 }}>
                        {r.product || <span style={{ color: "#b91c1c", fontStyle: "italic" }}>Missing product name</span>}
                      </td>
                      <td style={{ padding: "6px 8px", color: "var(--navy)" }}>
                        {r.quantity !== "" ? r.quantity : <span style={{ color: "#b91c1c" }}>—</span>}
                      </td>
                      <td style={{ padding: "6px 8px", color: "var(--muted)" }}>{r.unit}</td>
                      <td style={{ padding: "6px 8px", fontWeight: 600, color: r.isValid ? "#15803d" : "#b91c1c" }}>
                        {r.hasPrice ? formatCurrency(r.value) : <span style={{ color: "var(--muted)", fontWeight: 400 }}>₹0 (Not Added)</span>}
                      </td>
                      <td style={{ padding: "6px 8px", fontSize: 11 }}>
                        {r.isValid ? (
                          <span style={{ color: "#15803d", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 3 }}>
                            <Check size={12} /> Valid
                          </span>
                        ) : (
                          <span style={{ color: "#b91c1c", fontSize: 10 }} title={r.errors.join(", ")}>
                            ✗ {r.errors[0]}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal Actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
          <button
            type="button"
            className="outline-btn compact"
            onClick={onClose}
            disabled={submitting}
            data-testid="bulk-order-cancel-btn"
            style={{ height: 32, fontSize: 12, padding: "0 12px" }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="primary-btn compact"
            disabled={submitting || validRows.length === 0}
            onClick={handleConfirmUpload}
            data-testid="bulk-order-confirm-btn"
            style={{ height: 32, fontSize: 12, padding: "0 14px" }}
          >
            <CheckCircle2 size={13} />
            {submitting ? "Creating orders..." : `Confirm & Create ${validRows.length} Orders`}
          </button>
        </div>
      </div>
    </div>
  );
}
